import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useDepressionMode } from '../contexts/DepressionModeContext';
import AppLayout from '../components/AppLayout';
import PageTitle from '../components/PageTitle';
import LoadingSpinner from '../components/LoadingSpinner';
import api from '../api/api';
import '../styles/pages/ConsultationHistoryPage.css';

/**
 * 相談履歴ページ（V2 - つむぎデザイン準拠）
 */
const ConsultationHistoryPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { depressionMode } = useDepressionMode();
  const [consultations, setConsultations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');


  // ページタイトル設定
  useEffect(() => {
    document.title = '相談履歴 | つむぎ';
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    loadConsultations();
  }, [isAuthenticated, navigate]);

  const loadConsultations = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/consultations/history');
      if (response.data.success) {
        setConsultations(response.data.consultations || []);
      } else {
        setError('相談履歴の取得に失敗しました');
      }
    } catch (err) {
      console.error('Failed to load consultations:', err);
      setError('相談履歴の読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  };

  // ピン留め切り替え
  const togglePin = async (consultationId) => {
    try {
      const response = await api.patch(`/api/consultations/${consultationId}/pin`);
      if (response.data.success) {
        // 再読み込み
        loadConsultations();
      } else {
        alert('❌ ピン留めの更新に失敗しました');
      }
    } catch (error) {
      console.error('ピン留め更新エラー:', error);
      alert('❌ ピン留めの更新に失敗しました');
    }
  };

  // V2のプリセット質問に基づいてタイトルとアイコンを生成
  const generateConsultationTitle = (consultation) => {
    // 継続相談
    if (consultation.isFollowUp) {
      return { icon: '🔄', title: '前回相談の継続・追加質問', badge: '継続相談' };
    }

    // V2のpresetTypeが保存されている場合はそれを使用（優先）
    const presetType = consultation.presetType;
    if (presetType) {
      const presetMapping = {
        'morning_fatigue': { icon: '😴', title: '朝起きられない・一日中だるい', badge: '最も多い悩み' },
        'appetite_loss': { icon: '🍽️', title: '食欲がない・食べるのが億劫', badge: '人気' },
        'mental_health': { icon: '😔', title: '気分が優れない・元気が出ない', badge: 'メンタルケア' },
        'simple_easy': { icon: '🤷‍♀️', title: '何を食べたらいいか分からない', badge: '初めての方に' },
        'other': { icon: '💭', title: 'その他（自由に質問）', badge: null }
      };

      if (presetMapping[presetType]) {
        return presetMapping[presetType];
      }
    }

    // 後方互換性：presetTypeがない古いデータはテキスト解析（フォールバック）
    const consultationText = consultation.userInput?.consultation || '';
    const lowerText = consultationText.toLowerCase();

    // 朝起きられない・一日中だるい
    if (lowerText.includes('朝起きられない') || lowerText.includes('朝起きる') ||
        (lowerText.includes('一日中') && lowerText.includes('だるい')) ||
        (lowerText.includes('体が重') && lowerText.includes('だるい'))) {
      return { icon: '😴', title: '朝起きられない・一日中だるい', badge: '最も多い悩み' };
    }

    // 食欲がない・食べるのが億劫
    if (lowerText.includes('食欲がな') || lowerText.includes('食べるのが億劫') ||
        (lowerText.includes('食事') && lowerText.includes('億劫'))) {
      return { icon: '🍽️', title: '食欲がない・食べるのが億劫', badge: '人気' };
    }

    // 気分が優れない・元気が出ない
    if (lowerText.includes('気分が優れな') || lowerText.includes('元気が出な') ||
        lowerText.includes('気分が楽になる')) {
      return { icon: '😔', title: '気分が優れない・元気が出ない', badge: 'メンタルケア' };
    }

    // 何を食べたらいいか分からない
    if (lowerText.includes('何を食べたらいい') || lowerText.includes('何をどう食べれば') ||
        (lowerText.includes('バランスの良い食事') && lowerText.includes('具体的'))) {
      return { icon: '🤷‍♀️', title: '何を食べたらいいか分からない', badge: '初めての方に' };
    }

    // その他（自由に質問したい）
    return { icon: '💭', title: 'その他（自由に質問）', badge: null };
  };

  if (loading) {
    return (
      <AppLayout>
        <PageTitle title="相談履歴" icon="📋" />
        <LoadingSpinner message="相談履歴を読み込んでいます..." size="medium" />
      </AppLayout>
    );
  }

  if (error) {
    return (
      <AppLayout>
        <PageTitle title="相談履歴" subtitle="エラー" icon="⚠️" />
        <div className="tsumugi-container py-6">
          <div className="tsumugi-card text-center">
            <p className="consultation-history-page__error-text">{error}</p>
            <button
              onClick={() => navigate('/mypage')}
              className="tsumugi-btn tsumugi-btn-primary"
            >
              マイページへ戻る
            </button>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <PageTitle
        title="相談履歴"
        subtitle={`全${consultations.length}件`}
        icon="📋"
        mascot={<img src="/tsumugi.png" alt="つむぎ" className="consultation-history-page__mascot-image" />}
      />

      <div className="tsumugi-container py-6">
        {consultations.length === 0 ? (
          <div className="tsumugi-card">
            <div className="consultation-empty">
              <div className="consultation-empty__icon">
                <img src="/tsumugi.png" alt="" className="w-24 h-24" />
              </div>
              <p className="consultation-empty__title">まだ相談履歴がありません</p>
              <p className="consultation-empty__text">
                焦らなくて大丈夫。<br />
                気が向いた時に、いつでも相談してね
              </p>
              <button
                className="tsumugi-btn tsumugi-btn-primary consultation-empty__action"
                onClick={() => navigate('/form-v2')}
              >
                初めての相談をする
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="consultation-list">
              {consultations.map((consultation) => {
                const titleData = generateConsultationTitle(consultation);
                const consultationText = consultation.userInput?.consultation || '';
                const displayText = consultationText.length > 100
                  ? consultationText.substring(0, 100) + '...'
                  : consultationText;

                return (
                  <div
                    key={consultation._id}
                    className="consultation-item"
                  >
                    {/* ピン留めボタン */}
                    <button
                      className={`consultation-item__pin ${
                        consultation.isPinned ? 'consultation-item__pin--active' : ''
                      }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        togglePin(consultation._id);
                      }}
                      title={consultation.isPinned ? 'ピン留めを外す' : 'ピン留めする'}
                    >
                      📌
                    </button>

                    {/* 相談内容（クリック可能） */}
                    <div
                      className="consultation-item__content"
                      onClick={() => navigate(`/consultation/${consultation._id}`)}
                    >
                      <div className="consultation-item__header">
                        <div className="consultation-item__header-content">
                          <span className="consultation-item__icon">{titleData.icon}</span>
                          <span className="consultation-item__title">
                            {titleData.title}
                          </span>
                          {!depressionMode && titleData.badge && (
                            <span className="tsumugi-tag tsumugi-tag-lilac consultation-item__tag">
                              {titleData.badge}
                            </span>
                          )}
                        </div>
                        <span className="consultation-item__date">
                          {new Date(consultation.createdAt).toLocaleDateString('ja-JP', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </span>
                      </div>
                      {consultationText && consultationText.length > 20 && (
                        <p className="consultation-item__question">
                          {displayText}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* 戻るボタン */}
            <div className="consultation-history-page__back-section">
              <button
                onClick={() => navigate('/mypage')}
                className="tsumugi-btn tsumugi-btn-outline"
              >
                📱 マイページへ戻る
              </button>
            </div>
          </>
        )}
      </div>
    </AppLayout>
  );
};

export default ConsultationHistoryPage;
